import { create } from 'zustand';

import type { PackageEvidence } from '@cadstrom/cadstrom-shared';

/** How a pick of package rows reads to the viewer: a short name and, optionally, a line of detail beneath it. */
export interface EvidenceRowHeading {
	/** What the rows are, e.g. the parameter a datasheet row states. */
	title: string;
	/** Detail that tells the rows apart, e.g. a datasheet row's conditions and values; null when there is none. */
	subtitle: string | null;
}

/** Package rows the viewer picked, whose datasheet evidence the evidence panel shows. */
export interface EvidenceRowPick extends EvidenceRowHeading {
	/** The package the rows belong to. */
	packageId: string;
	/** The selected rows; never empty and at most {@link PackageEvidence.EVIDENCE_SELECTION_MAX_ROWS}. */
	selections: PackageEvidence.EvidenceRowSelection[];
}

/**
 * The viewer's current row pick, shared by the screens that pick rows and the panels that show their evidence, and
 * whether datasheet windows follow it.
 */
export interface EvidenceRowSelectionStore {
	/** The current pick, or null when no row is picked. */
	selection: EvidenceRowPick | null;
	/** Whether datasheet windows opened from evidence show the current pick's datasheet row; off unless turned on. */
	followSelection: boolean;
	/** Replaces the pick. */
	select: (selection: EvidenceRowPick) => void;
	/** Clears the pick. */
	clear: () => void;
	/** Turns following the pick in datasheet windows on or off. */
	setFollowSelection: (followSelection: boolean) => void;
}

/** The viewer's row pick and whether datasheet windows follow it; no row is picked and nothing follows at first. */
export const useEvidenceRowSelectionStore = create<EvidenceRowSelectionStore>((set) => ({
	selection: null,
	followSelection: false,
	select: (selection) => set({ selection }),
	clear: () => set({ selection: null }),
	setFollowSelection: (followSelection) => set({ followSelection }),
}));
